import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Settings, Globe, DollarSign, Mail, Bell, Shield, Database,
    Save, RefreshCw, Palette, Upload
} from 'lucide-react'
import { useLanguageStore } from '../../lib/store'

export default function SettingsPage() {
    useTranslation()
    const { language } = useLanguageStore()

    const [activeTab, setActiveTab] = useState('general')
    const [saving, setSaving] = useState(false)
    const [loading, setLoading] = useState(true)

    // Settings state
    const [settings, setSettings] = useState({
        site_name: 'Phú Quốc Rental',
        site_description: 'Best vehicle rental platform in Phú Quốc',
        contact_email: 'support@phuquoc-rental.com',
        contact_phone: '+84 123 456 789',
        default_language: 'vi',
        default_currency: 'VND',
        exchange_rate: '27000',
        commission_rate: '10',
        min_rental_days: '1',
        max_rental_days: '30',
        booking_advance_hours: '4',
        email_notifications: 'true',
        sms_notifications: 'false',
        maintenance_mode: 'false',
    })

    // Fetch settings from API
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await fetch('/api/admin/settings')
                const data = await res.json()
                if (data.success && data.raw) {
                    const newSettings: Record<string, string> = {}
                    for (const item of data.raw) {
                        newSettings[item.key] = item.value
                    }
                    setSettings(prev => ({ ...prev, ...newSettings }))
                }
            } catch (error) {
                console.error('Failed to fetch settings:', error)
            } finally {
                setLoading(false)
            }
        }
        fetchSettings()
    }, [])

    const tabs = [
        { id: 'general', name: { en: 'General', vi: 'Cài đặt chung' }, icon: Settings },
        { id: 'localization', name: { en: 'Localization', vi: 'Ngôn ngữ & Tiền tệ' }, icon: Globe },
        { id: 'booking', name: { en: 'Booking', vi: 'Đặt xe' }, icon: DollarSign },
        { id: 'notifications', name: { en: 'Notifications', vi: 'Thông báo' }, icon: Bell },
        { id: 'security', name: { en: 'Security', vi: 'Bảo mật' }, icon: Shield },
        { id: 'appearance', name: { en: 'Appearance', vi: 'Giao diện' }, icon: Palette },
    ]

    const handleSave = async () => {
        setSaving(true)
        try {
            const res = await fetch('/api/admin/settings', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(settings)
            })
            const data = await res.json()
            if (data.success) {
                alert(language === 'en' ? 'Settings saved successfully!' : 'Đã lưu cài đặt!')
            } else {
                alert(language === 'en' ? 'Failed to save settings' : 'Lỗi khi lưu cài đặt')
            }
        } catch (error) {
            console.error('Failed to save settings:', error)
            alert(language === 'en' ? 'Failed to save settings' : 'Lỗi khi lưu cài đặt')
        } finally {
            setSaving(false)
        }
    }

    const handleChange = (key: string, value: any) => {
        setSettings(prev => ({ ...prev, [key]: String(value) }))
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <RefreshCw className="w-8 h-8 animate-spin text-primary-600" />
            </div>
        )
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">
                        {language === 'en' ? 'Platform Settings' : 'Cài Đặt Hệ Thống'}
                    </h1>
                    <p className="text-gray-500 mt-1">
                        {language === 'en' ? 'Configure your platform settings' : 'Cấu hình cài đặt nền tảng'}
                    </p>
                </div>
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition disabled:opacity-50"
                >
                    {saving ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                    {language === 'en' ? 'Save Changes' : 'Lưu thay đổi'}
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                {/* Sidebar Tabs */}
                <div className="bg-white rounded-xl border p-2">
                    {tabs.map((tab) => {
                        const Icon = tab.icon
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition ${activeTab === tab.id
                                    ? 'bg-primary-50 text-primary-700 font-medium'
                                    : 'hover:bg-gray-50 text-gray-700'
                                    }`}
                            >
                                <Icon className="w-5 h-5" />
                                {tab.name[language]}
                            </button>
                        )
                    })}
                </div>

                {/* Settings Content */}
                <div className="lg:col-span-3 bg-white rounded-xl border p-6">
                    {activeTab === 'general' && (
                        <div className="space-y-6">
                            <h2 className="text-lg font-bold border-b pb-3">
                                {language === 'en' ? 'General Settings' : 'Cài Đặt Chung'}
                            </h2>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium mb-2">
                                        {language === 'en' ? 'Site Name' : 'Tên website'}
                                    </label>
                                    <input
                                        type="text"
                                        value={settings.site_name}
                                        onChange={(e) => handleChange('site_name', e.target.value)}
                                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-2">
                                        {language === 'en' ? 'Support Email' : 'Email hỗ trợ'}
                                    </label>
                                    <input
                                        type="email"
                                        value={settings.contact_email}
                                        onChange={(e) => handleChange('contact_email', e.target.value)}
                                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-2">
                                        {language === 'en' ? 'Support Phone' : 'Điện thoại hỗ trợ'}
                                    </label>
                                    <input
                                        type="tel"
                                        value={settings.contact_phone}
                                        onChange={(e) => handleChange('contact_phone', e.target.value)}
                                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-2">
                                        {language === 'en' ? 'Site Description' : 'Mô tả website'}
                                    </label>
                                    <input
                                        type="text"
                                        value={settings.site_description}
                                        onChange={(e) => handleChange('site_description', e.target.value)}
                                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-2">
                                    {language === 'en' ? 'Logo' : 'Logo'}
                                </label>
                                <div className="border-2 border-dashed rounded-lg p-8 text-center">
                                    <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                                    <p className="text-sm text-gray-500">
                                        {language === 'en' ? 'Click or drag to upload logo' : 'Click hoặc kéo thả để tải lên logo'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'localization' && (
                        <div className="space-y-6">
                            <h2 className="text-lg font-bold border-b pb-3">
                                {language === 'en' ? 'Localization' : 'Ngôn Ngữ & Tiền Tệ'}
                            </h2>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium mb-2">
                                        {language === 'en' ? 'Default Language' : 'Ngôn ngữ mặc định'}
                                    </label>
                                    <select
                                        value={settings.default_language}
                                        onChange={(e) => handleChange('default_language', e.target.value)}
                                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                                    >
                                        <option value="vi">Tiếng Việt</option>
                                        <option value="en">English</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-2">
                                        {language === 'en' ? 'Default Currency' : 'Tiền tệ mặc định'}
                                    </label>
                                    <select
                                        value={settings.default_currency}
                                        onChange={(e) => handleChange('default_currency', e.target.value)}
                                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                                    >
                                        <option value="VND">VND (₫)</option>
                                        <option value="USD">USD ($)</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-2">
                                        {language === 'en' ? 'Exchange Rate (VND/USD)' : 'Tỷ giá (VND/USD)'}
                                    </label>
                                    <input
                                        type="number"
                                        value={settings.exchange_rate}
                                        onChange={(e) => handleChange('exchange_rate', e.target.value)}
                                        placeholder="27000"
                                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        {language === 'en' ? 'Used for currency conversion (e.g., 27000 = 1 USD = 27,000 VND)' : 'Dùng để quy đổi tiền tệ (VD: 27000 = 1 USD = 27,000 VND)'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'booking' && (
                        <div className="space-y-6">
                            <h2 className="text-lg font-bold border-b pb-3">
                                {language === 'en' ? 'Booking Settings' : 'Cài Đặt Đặt Xe'}
                            </h2>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium mb-2">
                                        {language === 'en' ? 'Platform Commission (%)' : 'Phí hoa hồng (%)'}
                                    </label>
                                    <input
                                        type="number"
                                        value={settings.commission_rate}
                                        onChange={(e) => handleChange('commission_rate', parseInt(e.target.value))}
                                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-2">
                                        {language === 'en' ? 'Min Booking Hours' : 'Số giờ thuê tối thiểu'}
                                    </label>
                                    <input
                                        type="number"
                                        value={settings.booking_advance_hours}
                                        onChange={(e) => handleChange('booking_advance_hours', parseInt(e.target.value))}
                                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-2">
                                        {language === 'en' ? 'Max Booking Days' : 'Số ngày thuê tối đa'}
                                    </label>
                                    <input
                                        type="number"
                                        value={settings.max_rental_days}
                                        onChange={(e) => handleChange('max_rental_days', parseInt(e.target.value))}
                                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                                    />
                                </div>
                            </div>

                            <div className="space-y-4">
                                <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                    <div>
                                        <p className="font-medium">{language === 'en' ? 'Auto-approve Vehicles' : 'Tự động duyệt xe'}</p>
                                        <p className="text-sm text-gray-500">{language === 'en' ? 'New vehicles will be approved automatically' : 'Xe mới sẽ được duyệt tự động'}</p>
                                    </div>
                                    <input
                                        type="checkbox"
                                        checked={(settings as any).auto_approve_vehicles === 'true'}
                                        onChange={(e) => handleChange('auto_approve_vehicles', e.target.checked ? 'true' : 'false')}
                                        className="w-5 h-5 text-primary-600 rounded"
                                    />
                                </label>
                                <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                    <div>
                                        <p className="font-medium">{language === 'en' ? 'Auto-approve Reviews' : 'Tự động duyệt đánh giá'}</p>
                                        <p className="text-sm text-gray-500">{language === 'en' ? 'New reviews will be published automatically' : 'Đánh giá mới sẽ được đăng tự động'}</p>
                                    </div>
                                    <input
                                        type="checkbox"
                                        checked={(settings as any).auto_approve_reviews === 'true'}
                                        onChange={(e) => handleChange('auto_approve_reviews', e.target.checked ? 'true' : 'false')}
                                        className="w-5 h-5 text-primary-600 rounded"
                                    />
                                </label>
                            </div>
                        </div>
                    )}

                    {activeTab === 'notifications' && (
                        <div className="space-y-6">
                            <h2 className="text-lg font-bold border-b pb-3">
                                {language === 'en' ? 'Notification Settings' : 'Cài Đặt Thông Báo'}
                            </h2>

                            <div className="space-y-4">
                                <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                    <div className="flex items-center gap-3">
                                        <Mail className="w-5 h-5 text-gray-500" />
                                        <div>
                                            <p className="font-medium">{language === 'en' ? 'Email Notifications' : 'Thông báo Email'}</p>
                                            <p className="text-sm text-gray-500">{language === 'en' ? 'Send email notifications to users' : 'Gửi thông báo qua email'}</p>
                                        </div>
                                    </div>
                                    <input
                                        type="checkbox"
                                        checked={settings.email_notifications === 'true'}
                                        onChange={(e) => handleChange('email_notifications', e.target.checked ? 'true' : 'false')}
                                        className="w-5 h-5 text-primary-600 rounded"
                                    />
                                </label>
                                <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                    <div className="flex items-center gap-3">
                                        <Bell className="w-5 h-5 text-gray-500" />
                                        <div>
                                            <p className="font-medium">{language === 'en' ? 'SMS Notifications' : 'Thông báo SMS'}</p>
                                            <p className="text-sm text-gray-500">{language === 'en' ? 'Send SMS notifications to users' : 'Gửi thông báo qua SMS'}</p>
                                        </div>
                                    </div>
                                    <input
                                        type="checkbox"
                                        checked={settings.sms_notifications === 'true'}
                                        onChange={(e) => handleChange('sms_notifications', e.target.checked ? 'true' : 'false')}
                                        className="w-5 h-5 text-primary-600 rounded"
                                    />
                                </label>
                            </div>
                        </div>
                    )}

                    {activeTab === 'security' && (
                        <div className="space-y-6">
                            <h2 className="text-lg font-bold border-b pb-3">
                                {language === 'en' ? 'Security Settings' : 'Cài Đặt Bảo Mật'}
                            </h2>

                            <div className="space-y-4">
                                <label className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-200">
                                    <div className="flex items-center gap-3">
                                        <Database className="w-5 h-5 text-red-500" />
                                        <div>
                                            <p className="font-medium text-red-700">{language === 'en' ? 'Maintenance Mode' : 'Chế độ bảo trì'}</p>
                                            <p className="text-sm text-red-500">{language === 'en' ? 'Only admins can access the site' : 'Chỉ admin có thể truy cập'}</p>
                                        </div>
                                    </div>
                                    <input
                                        type="checkbox"
                                        checked={settings.maintenance_mode === 'true'}
                                        onChange={(e) => handleChange('maintenance_mode', e.target.checked ? 'true' : 'false')}
                                        className="w-5 h-5 text-red-600 rounded"
                                    />
                                </label>
                            </div>

                            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                                <p className="font-medium text-yellow-800 mb-2">⚠️ {language === 'en' ? 'Danger Zone' : 'Khu vực nguy hiểm'}</p>
                                <p className="text-sm text-yellow-700 mb-3">
                                    {language === 'en' ? 'These actions are irreversible' : 'Các hành động này không thể hoàn tác'}
                                </p>
                                <button className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700">
                                    {language === 'en' ? 'Clear All Cache' : 'Xóa toàn bộ cache'}
                                </button>
                            </div>
                        </div>
                    )}

                    {activeTab === 'appearance' && (
                        <div className="space-y-6">
                            <h2 className="text-lg font-bold border-b pb-3">
                                {language === 'en' ? 'Appearance Settings' : 'Cài Đặt Giao Diện'}
                            </h2>

                            <div>
                                <label className="block text-sm font-medium mb-3">
                                    {language === 'en' ? 'Primary Color' : 'Màu chủ đạo'}
                                </label>
                                <div className="flex gap-3">
                                    {['#059669', '#2563eb', '#7c3aed', '#dc2626', '#ea580c'].map((color) => (
                                        <button
                                            key={color}
                                            className="w-10 h-10 rounded-full border-2 border-gray-200 hover:scale-110 transition"
                                            style={{ backgroundColor: color }}
                                        />
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-3">
                                    {language === 'en' ? 'Theme' : 'Chủ đề'}
                                </label>
                                <div className="grid grid-cols-3 gap-4">
                                    <button className="p-4 border-2 border-primary-500 rounded-lg text-center">
                                        <div className="w-full h-8 bg-white rounded mb-2 border"></div>
                                        <span className="text-sm">{language === 'en' ? 'Light' : 'Sáng'}</span>
                                    </button>
                                    <button className="p-4 border rounded-lg text-center hover:border-gray-400">
                                        <div className="w-full h-8 bg-gray-900 rounded mb-2"></div>
                                        <span className="text-sm">{language === 'en' ? 'Dark' : 'Tối'}</span>
                                    </button>
                                    <button className="p-4 border rounded-lg text-center hover:border-gray-400">
                                        <div className="w-full h-8 bg-gradient-to-r from-white to-gray-900 rounded mb-2"></div>
                                        <span className="text-sm">{language === 'en' ? 'Auto' : 'Tự động'}</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
